import { type ReactNode, useState } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import { AuthTypes, ProofRequirementMfa, SigningRequirementEnum } from "graphql_globals";
import { VerticalStepIndicator } from "common/signer/common";
import { MasterPrivacyPolicyUrl, UserTermsOfService } from "common/tos";
import { Row, Container, useMobileScreenClass } from "common/core/responsive";
import { Heading, Paragraph } from "common/core/typography";
import EsignIllustration from "assets/images/signing-order-illustration.svg";
import IdentityShield from "assets/images/signer/id-shield.svg";
import Svg from "common/core/svg";
import Button from "common/core/button";
import { ProofFooter } from "common/proof_footer";
import { isCertify, isIdentify } from "util/transaction";

import { useHandleReadyToSign, LandingWrapper } from "./common";
import LandingContacts from "./contacts";
import LandingCustomBlock from "./custom_block";
import FrequentlyAskedQuestions from "./faqs";
import Styles from "./common.module.scss";
import type {
  SignerLanding_viewer as Viewer,
  SignerLanding_documentBundle_DocumentBundle as DocumentBundle,
} from "../index.query.graphql";

type Props = {
  documentBundle: DocumentBundle;
  viewer: Viewer;
  readOnly?: boolean;
  isEsign?: boolean;
  handleBlockedAction: (action: () => void) => void;
  isProofRequirement?: boolean;
};

type InnerProps = Props & {
  renderExpiredOrInactiveContent: () => ReactNode;
};

function ContinueAction({
  isLoading,
  onContinue,
  isMobile,
}: {
  isLoading: boolean;
  onContinue: () => void;
  isMobile?: boolean;
}) {
  return (
    <div className={isMobile ? Styles.mobileFooter : Styles.esignDocumentMessage}>
      <Button
        automationId="get-started-button"
        onClick={onContinue}
        isLoading={isLoading}
        buttonColor="action"
        fullwidth={isMobile}
        buttonSize="large"
        variant="primary"
      >
        <FormattedMessage id="d0259998-9240-4272-bff3-2b7d20301229" defaultMessage="Sign now" />
      </Button>
    </div>
  );
}

function Subheading({
  brandName,
  totalDocumentsCount,
  isProofRequirement,
  identifyTransaction,
}: {
  brandName: string;
  totalDocumentsCount: number;
  isProofRequirement?: boolean;
  identifyTransaction: boolean;
}) {
  return (
    <Heading level="h2" textStyle="headingFour" className={Styles.subheader}>
      {identifyTransaction || isProofRequirement ? (
        <FormattedMessage
          id="07f84068-20e9-4f63-b107-ab26c12bee21"
          defaultMessage="Let's verify your identity"
        />
      ) : (
        <FormattedMessage
          id="5a598c19-c4d0-49df-82eb-263233464635"
          defaultMessage="{brandName} has sent you {totalDocumentsCount, plural, one{a document} other{documents}} to sign online."
          values={{ brandName, totalDocumentsCount }}
        />
      )}
    </Heading>
  );
}

function EsignLanding({
  documentBundle,
  viewer,
  readOnly,
  renderExpiredOrInactiveContent,
  handleBlockedAction,
  isProofRequirement,
}: InnerProps) {
  const [isLoading, setLoading] = useState(false);
  const { documents, organizationTransaction, kbaRequired, participants } = documentBundle;
  const { totalCount: totalDocumentsCount } = documents;
  const organizationBrand = organizationTransaction.publicOrganization.organizationBrand;
  const organizationBrandName = organizationBrand.name;
  const brandName = organizationBrandName;
  const { requiredAuth } = organizationTransaction;
  const participant = participants!.find((p) => p!.userId === viewer.user!.id)!;
  const userParticipantIsEsign = participant.signingRequirement === SigningRequirementEnum.ESIGN;
  const mfaRequired = userParticipantIsEsign
    ? participant.proofRequirement?.mfa?.type === ProofRequirementMfa.SMS
    : requiredAuth === AuthTypes.SMS ||
      participant.proofRequirement?.mfa?.type === ProofRequirementMfa.SMS;
  const hasKbaRequirement = userParticipantIsEsign
    ? participant.proofRequirement?.kba
    : kbaRequired || participant.proofRequirement?.kba;
  const hasCredentialAnalysisProofRequirement =
    isProofRequirement || !!participant.proofRequirement?.ca;
  const handleContinue = useHandleReadyToSign(documentBundle, viewer, readOnly);
  const isMobile = useMobileScreenClass();
  const identifyTransaction = isIdentify(organizationTransaction);
  const certifyTransaction = isCertify(organizationTransaction);

  async function onContinue() {
    setLoading(true);
    await handleContinue();
    setLoading(false);
  }

  const expiredOrInactive = renderExpiredOrInactiveContent();

  const renderBody = () => {
    if (expiredOrInactive) {
      return expiredOrInactive;
    }

    const generateListItems = () => {
      const esignDocument = {
        item: (
          <FormattedMessage
            id="a78380b0-cec2-4240-a7a8-634b789f4b9e"
            defaultMessage="eSign your {totalDocumentsCount, plural, one{document} other{documents}}"
            values={{ totalDocumentsCount }}
          />
        ),
      };
      const enterDetails = {
        item: (
          <FormattedMessage
            id="20a39421-61da-4b78-8f99-9c0cfe793833"
            defaultMessage="Verify your information"
          />
        ),
      };
      const confirmIdentity = {
        item: (
          <FormattedMessage
            id="fbfcfee2-3b73-454d-b78c-1aef3811056b"
            defaultMessage="Verify your identity"
          />
        ),
      };
      const verifyPhoneNumber = {
        item: (
          <FormattedMessage
            id="72cf66fc-c350-4a34-b6e6-fdca05bdc2f7"
            defaultMessage="Verify your phone number"
          />
        ),
      };
      const genericEsign =
        !hasKbaRequirement && !mfaRequired && !hasCredentialAnalysisProofRequirement;
      const requiresVerification = hasKbaRequirement || hasCredentialAnalysisProofRequirement;
      if (certifyTransaction || genericEsign) {
        return [];
      }
      if (identifyTransaction) {
        return [enterDetails, confirmIdentity];
      }
      const reqs = [
        mfaRequired ? verifyPhoneNumber : null,
        requiresVerification ? enterDetails : null,
        requiresVerification ? confirmIdentity : null,
        esignDocument,
      ];
      return reqs.filter((req) => req !== null);
    };

    const listItems = generateListItems();

    return (
      <>
        <div className={Styles.documentCanvas}>
          <div
            className={classnames(Styles.esignDocumentContainer, Styles.documentContainerNoMargin)}
          >
            <Svg
              src={
                identifyTransaction || certifyTransaction || isProofRequirement
                  ? IdentityShield
                  : EsignIllustration
              }
              className={Styles.image}
              alt=""
            />
            <Subheading
              isProofRequirement={isProofRequirement}
              identifyTransaction={identifyTransaction}
              brandName={brandName}
              totalDocumentsCount={totalDocumentsCount}
            />
            <Paragraph size="large" className={Styles.subheaderInfo}>
              <FormattedMessage
                id="3da8075a-42c4-462d-87f5-95ee5a64d9d8"
                defaultMessage="This will take about {time} minutes to complete. "
                values={{ time: hasKbaRequirement ? "5-10" : "5" }}
              />
            </Paragraph>
            {listItems.length > 0 && (
              <div className={Styles.steps}>
                <VerticalStepIndicator
                  className={Styles.stepIndicator}
                  numberedSteps
                  listItems={listItems}
                />
              </div>
            )}

            {!isMobile && (
              <ContinueAction
                isLoading={isLoading}
                onContinue={() => handleBlockedAction(onContinue)}
              />
            )}
          </div>
        </div>
        <div className={Styles.termsEsign}>
          <Paragraph size="small" textColor="subtle" textAlign="center">
            <FormattedMessage
              id="c462acc4-5182-4f49-9a54-a7bcd17e8543"
              defaultMessage="By clicking 'Sign now', you are agreeing to the {termsOfUse}. For information on our privacy and data use practices please see our {privacyPolicy}."
              values={{
                termsOfUse: <UserTermsOfService underlined />,
                privacyPolicy: <MasterPrivacyPolicyUrl underlined />,
              }}
            />
          </Paragraph>
        </div>
      </>
    );
  };

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.containerWrapper}>
        <Container className={Styles.container}>
          <Row>{renderBody()}</Row>
        </Container>
        <LandingCustomBlock organizationBrand={organizationBrand} />
        <LandingContacts contacts={organizationTransaction.contacts} />
        <FrequentlyAskedQuestions />
        <ProofFooter />
      </div>
      {isMobile && (
        <ContinueAction
          isMobile
          isLoading={isLoading}
          onContinue={() => handleBlockedAction(onContinue)}
        />
      )}
    </div>
  );
}

function EsignLandingWrapper(props: Props) {
  return (
    <LandingWrapper automationPrefix="esign-landing" {...props}>
      {(renderExpiredOrInactiveContent) => (
        <EsignLanding renderExpiredOrInactiveContent={renderExpiredOrInactiveContent} {...props} />
      )}
    </LandingWrapper>
  );
}

export { EsignLandingWrapper as EsignLanding };
